import React, { useEffect } from 'react';
import { useCurrentInvestigation } from '../../../../hooks';
import { IAgenda, IAppointment, IAgendasConfigParams, IPatient, AgendaScheduleTypes, OutpatientsVisualizationMode, AgendasTypes } from '../../../../constants/types';
import usePageVisibility, { SnackbarType, useSnackBarState } from '../../../../hooks';
import { mergeAppointmentAfterCheckIn } from '../../../../utils/patientAppointmentTable';
import { cancelAppointmentService, getAppoinmentsDateService, updateAppoinmentsService } from '../../../../services/agenda';
import { AppointmentsDate } from './AppointmentsDate';
import MultiAgenda from './MultiAgenda';
import { errorCodesCreateAppointment } from '../../../../utils/agenda';
import { IRequest, RequestStatus } from '../../Request/types';
import { incrementalSyncPatientsAction } from '../../../../redux/actions/patientsActions';
import { useDispatch, useSelector } from 'react-redux';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { Button, Grid, IconButton, Tooltip, Typography, CircularProgress } from '@mui/material';
import { Translate } from 'react-localize-redux';
import Modal from '../../../../components/general/modal';
import { useVoucherBillingConfirm } from '../../../../hooks/useVoucherBillingConfirm';
import SurgerySchedulePDFDocument from '../../../../components/pdf/documents/SurgerySchedulePDFDocument';
import { useSurgeryWeekPDF } from '../../../../hooks/useSurgeryWeekPDF';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

interface AppointmentsProps {
    uuidInvestigation: string;
    patientsPersonalData: IPatient[];
    uuidAgendas: string[];
    extraForm?: number;
    typeAgenda:AgendasTypes;
    agendas:IAgenda[];
    dateSelected: Date;
    canCreateAppointments: boolean;
    request:IRequest | null;
    appointment:IAppointment | null;
    type: AgendaScheduleTypes;
    mode:OutpatientsVisualizationMode,
    shouldReloadOnTabChange:boolean,
    preSelectedPatientUuid?: string;
    timezone?: string;
    callbackAppointments?: (appointments:IAppointment[]) => void;
    onAppointmentCreated?: (appointment:IAppointment) => void;
}

const Appointments: React.FC<AppointmentsProps> = ({ uuidInvestigation, mode, uuidAgendas, dateSelected,agendas, canCreateAppointments,
                                                    typeAgenda, type, shouldReloadOnTabChange, patientsPersonalData, appointment, extraForm, callbackAppointments, request, preSelectedPatientUuid, timezone, onAppointmentCreated }) => {
    const [loadingAppointments, setLoadingAppointments] = React.useState(false);
    const [appointments, setAppointments] = React.useState<IAppointment[]>([]);
    const [showSnackbar, setShowSnackbar] = useSnackBarState();
    const [loadingSingleAppointment, setLoadingSingleAppointment] = React.useState(false);
    const [lastUpdate, setLastUpdate] = React.useState(new Date());
    const [showModal, setShowModal] = React.useState(false);
    const dispatch = useDispatch();
    const [showPDFModal, setShowPDFModal] = React.useState(false);
    // SHER-395: shared "no forfait session → bill the consultation?" confirm dialog + retry.
    const { handleCheckInError, voucherConfirmModal } = useVoucherBillingConfirm();

    // Surgery PDF hook — only used when typeAgenda is SURGERY
    const billingInfo = useSelector((state: any) => state.investigations.currentInvestigation?.billingInfo);
    const currentInvestigation = useSelector((state: any) => state.investigations.currentInvestigation);
    // SHER-1101: gate the surgery-schedule PDF trigger behind the tenant functionality.
    const { isPdfExportEnabled } = useCurrentInvestigation();
    const researchers = useSelector((state: any) => state.hospital?.data?.researchers || []);

    const pdfConfig = React.useMemo(() => {
        if (!billingInfo || typeAgenda !== AgendasTypes.SURGERY) return null;
        return {
            hospitalName: currentInvestigation?.name || billingInfo.hospitalName,
            logoBlob: billingInfo.logoBlob,
            locale: billingInfo.locale || 'fr',
            address: billingInfo.address,
            city: billingInfo.city,
            email: billingInfo.email,
            phone: billingInfo.phone,
        };
    }, [billingInfo, currentInvestigation, typeAgenda]);

    const surgeryPDF = useSurgeryWeekPDF({
        uuidInvestigation,
        agendas,
        patientsPersonalData,
        researchers,
        locale: pdfConfig?.locale || 'fr',
    });

    async function handleSurgeryPDFClick() {
        const success = await surgeryPDF.fetchWeekData(dateSelected);
        if (success) {
            setShowPDFModal(true);
        } else {
            setShowSnackbar({show:true, message:"general.error", severity:"error"});
        }
    }

    function updateAppointment(uuidAppointment:string, forceBilling:boolean = false){
        setLoadingSingleAppointment(true);
        updateAppoinmentsService(uuidInvestigation, uuidAppointment, forceBilling)
            .then(response => {
                const indexAppointment = appointments.findIndex((appointment) => appointment.uuid === uuidAppointment);
                if(indexAppointment !== -1){
                    const updated = [...appointments];
                    updated[indexAppointment] = mergeAppointmentAfterCheckIn(
                        appointments[indexAppointment],
                        response.appointment,
                    );
                    setAppointments(updated);
                }
                setShowSnackbar({show:true, message:"pages.hospital.outpatients.checkin_success", severity:"success"});
                setLoadingSingleAppointment(false);
                resetModal();
                setLastUpdate(new Date());
            })
            .catch(err => {
                // SHER-395: no available forfait session → confirm dialog, then retry with
                // forceBilling (shared hook). Only on the first attempt to avoid a loop.
                if(!forceBilling && handleCheckInError(err, () => updateAppointment(uuidAppointment, true))){
                    setLoadingSingleAppointment(false);
                    return;
                }
                let message = "general.error";
                if(err.response.status === 401){
                    message = "pages.hospital.outpatients.no_permissions";
                }
                else if(err.response.data.errorCode === 1){
                    message = "pages.hospital.outpatients.error_ready_surgery";
                }
                else if(err.response.data.errorCode === "PATIENT_NOT_PRICE"){
                    message = "pages.hospital.outpatients.appointment.error.patient_not_insurance";
                }
                else if(err.response.data.errorCode === "PATIENT_NOT_VOUCHER"){
                    // Reached only on a forced retry that the API still rejects (the first
                    // attempt is intercepted above and shown as the confirm dialog).
                    message = "pages.hospital.outpatients.appointment.error.patient_not_voucher";
                }

                setShowSnackbar({show:true, message:message, severity:"error"});
                setLoadingSingleAppointment(false);
            })
    }
    function resetModal(){
        setShowModal(false);
    }
    function callbackResetModal(){
        resetModal();
    }
    function cancelAppointment(uuidAppointment:string, byUser:boolean = false){
        setLoadingAppointments(true);
        cancelAppointmentService(uuidInvestigation, uuidAppointment, byUser)
            .then(response => {
                const indexAppointment = appointments.findIndex((appointment) => appointment.uuid === uuidAppointment);
                if(!byUser){
                    appointments.splice(indexAppointment, 1);
                }
                else{
                    appointments[indexAppointment].requestAppointment.status = RequestStatus.CANCELED_BY_USER;
                }
                setShowSnackbar({show:true, message:"pages.hospital.outpatients.cancel_success", severity:"success"});
                setLoadingAppointments(false);
                setLastUpdate(new Date());
            })
            .catch(err => {
                setShowSnackbar({show:true, message:"general.error", severity:"error"});
                setLoadingAppointments(false);
                setLastUpdate(new Date());
            })
    }

    function appointmentCreatedCallback(appointment:IAppointment){
        setAppointments([...appointments, appointment]);
        setShowSnackbar({show:true, message:"pages.hospital.outpatients.calendar.new_appointment.success", severity:"success"});
        setLastUpdate(new Date());
        
        // Call the parent callback if provided (for URL cleanup)
        if (onAppointmentCreated) {
            onAppointmentCreated(appointment);
        }
    }

    function appointmentUpdatedCallback(updatedAppointment:IAppointment){
        // Find and update the existing appointment in the array
        const updatedAppointments = appointments.map(app => 
            app.uuid === updatedAppointment.uuid ? updatedAppointment : app
        );
        setAppointments(updatedAppointments);
        setLastUpdate(new Date());
        
        // Call the parent callback if provided
        if (onAppointmentCreated) {
            onAppointmentCreated(updatedAppointment);
        }
    }

    function appointmentErrorCallback(error:any){
        if(error.errorCode){
            setShowSnackbar({show:true, message:errorCodesCreateAppointment(error.errorCode), severity:"error"});    
        }
    }

    async function getAllAgendas(){
        let appointmentsNew: IAppointment[] = [];
        for(let i = 0; i < uuidAgendas.length; i++){
            const uuidAgenda = uuidAgendas[i];
            await getAppoinmentsDate(uuidAgenda, dateSelected, appointmentsNew)
        }
        setLastUpdate(new Date());
        setAppointments(appointmentsNew);
    }

    useEffect(() => {
        if (uuidAgendas.length > 0 && dateSelected) {
            getAllAgendas();
        }
    }, [uuidAgendas, dateSelected]);

    useEffect(() => {
        if (appointment) {
            const existingIndex = appointments.findIndex(a => a.uuid === appointment.uuid);
            if (existingIndex === -1) {
                setAppointments([...appointments, appointment]);
            }
        }
    }, [appointment]);


    usePageVisibility(() => {
        const fetchData = async () => {
          try {
            // Use incremental sync (event-based) for faster updates
            // Falls back to full sync automatically if needed
            await dispatch(incrementalSyncPatientsAction(uuidInvestigation, true));
          } catch (error) {
            console.error('Error syncing patients:', error);
          }
        };
        
        if(uuidAgendas && dateSelected && shouldReloadOnTabChange){
            getAllAgendas();
        }
        if(shouldReloadOnTabChange){
            fetchData();
        }
      });

    async function getAppoinmentsDate(uuidAgendas:string, date:Date, appointmentsExt:IAppointment[]){
        setLoadingAppointments(true);
        try {
            const response = await getAppoinmentsDateService(uuidInvestigation, uuidAgendas, date);
            appointmentsExt.push(...response.appointments); // Modify appointmentsExt directly
            if (callbackAppointments) {
                callbackAppointments(response.appointments);
            }
        } catch (err) {
            // Handle error if needed
        } finally {
            setLoadingAppointments(false);
        }
    }

    const appointmentPatientUuidSet = React.useMemo(
        () => new Set(appointments.map((appointment) => appointment.patient.uuid)),
        [appointments]
    );

    const filteredPatients = React.useMemo(
        () => patientsPersonalData.filter((patient) => appointmentPatientUuidSet.has(patient.uuid)),
        [patientsPersonalData, appointmentPatientUuidSet]
    );
    
    if(type === AgendaScheduleTypes.DATE_TIME){
        const filteredAgendas = agendas.filter(agenda => uuidAgendas.includes(agenda.uuid));

        const weekRangeStr = surgeryPDF.weekRange.start.toISOString().split('T')[0];
        const pdfFileName = `programme-chirurgical-${weekRangeStr}.pdf`;

        return (
            <>
                {typeAgenda === AgendasTypes.SURGERY && (
                    <>
                        {isPdfExportEnabled() && <Tooltip title={<Translate id="pages.hospital.surgery.schedule_pdf.download" />}>
                            <IconButton
                                onClick={handleSurgeryPDFClick}
                                disabled={surgeryPDF.loading}
                                size="small"
                                style={{ marginLeft: 8 }}
                            >
                                {surgeryPDF.loading ? <CircularProgress size={20} /> : <PictureAsPdfIcon />}
                            </IconButton>
                        </Tooltip>}
                        <Modal
                            open={showPDFModal}
                            closeModal={() => setShowPDFModal(false)}
                            title={<Translate id="pages.hospital.surgery.schedule_pdf.title" />}
                        >
                            <Grid container spacing={2} style={{ padding: '1rem' }}>
                                <Grid item xs={12}>
                                    <Typography variant="body1">
                                        <Translate id="pages.hospital.surgery.schedule_pdf.week_of" />{' '}
                                        {surgeryPDF.weekRange.start.toLocaleDateString(pdfConfig?.locale)} - {surgeryPDF.weekRange.end.toLocaleDateString(pdfConfig?.locale)}
                                        {' '}({surgeryPDF.weekData.reduce((sum, g) => sum + g.rows.length, 0)} <Translate id="general.patient" />)
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    {pdfConfig && (
                                        <PDFDownloadLink
                                            document={
                                                <SurgerySchedulePDFDocument
                                                    weekData={surgeryPDF.weekData}
                                                    weekRange={surgeryPDF.weekRange}
                                                    config={pdfConfig}
                                                />
                                            }
                                            fileName={pdfFileName}
                                            style={{ textDecoration: 'none' }}
                                        >
                                            {({ loading }) => (
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    disabled={loading}
                                                    fullWidth
                                                >
                                                    {loading ? <Translate id="general.loading.loading" /> : <Translate id="general.download_pdf" />}
                                                </Button>
                                            )}
                                        </PDFDownloadLink>
                                    )}
                                </Grid>
                            </Grid>
                        </Modal>
                    </>
                )}
                <MultiAgenda key={`${dateSelected.getUTCMilliseconds()}-${appointments.length}`} date={dateSelected} agendas={filteredAgendas} appointments={appointments}
                    patients={filteredPatients} showSnackbar={showSnackbar} lastUpdate={lastUpdate.getTime()}
                    uuidInvestigation={uuidInvestigation}
                    typeAgenda={typeAgenda} request={request}
                    appointment={appointment}
                    extraForm={extraForm} appointmentCreatedCallback={appointmentCreatedCallback}
                    appointmentUpdatedCallback={appointmentUpdatedCallback}
                    appointmentErrorCallback={appointmentErrorCallback} canCreateAppointments={canCreateAppointments}
                    preSelectedPatientUuid={preSelectedPatientUuid}
                    callbackSetSnackbar={(showSnackbar:SnackbarType) => setShowSnackbar(showSnackbar)}
                    cancelCallback={cancelAppointment} showUpCallback={updateAppointment} timezone={timezone} />
                {voucherConfirmModal}
            </>
        );
    }
    return (
        <>
            <AppointmentsDate loadingAppointments={loadingAppointments} appointments={appointments}
                loadingSingleAppointment={loadingSingleAppointment} showSnackbar={showSnackbar}
                typeAgenda={typeAgenda} dateSelected={dateSelected}
                callbackResetModal={callbackResetModal} mode={mode} patientsPersonalData={patientsPersonalData}
                callbackSetSnackbar={(showSnackbar:SnackbarType) => setShowSnackbar(showSnackbar)}
                callbackCancelAppointment={(uuidAppointment) => cancelAppointment(uuidAppointment)}
                callbackUpdateAppointment={(uuidAppointment) => updateAppointment(uuidAppointment)}
                timezone={timezone}
                />
            {voucherConfirmModal}
        </>
    );
};

export default Appointments;
